import React, { useState } from "react";
import {
  Box,
  Container,
  MenuItem,
  Select,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import { ComponentConfig } from "../Config/ComponentConfig.js";
import { Colors } from "../Constants/Colors";
import HeaderWithBackButton from "../components/Common/HeaderWithBackButton.tsx";
import { useLocation, useNavigate } from "react-router-dom";
import PrimaryButton from "../components/Common/PrimaryButton.tsx";
import axios from "axios";
import { toast } from "react-toastify";
import { WebRoutes } from "../Routes/WebRoutes.js";

const FollowUpQuery: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const data = location.state || {};
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (remarks.length > 100) {
      alert("Remarks should not exceed 100 characters.");
      return;
    }

    if(remarks.length === 0) {
        alert("Please add remarks.");
        return;
    }

    const formData = new FormData();
    formData.append("customer_ticket[original_ticket_id]", data?.previousQueryId || "");
    formData.append("customer_ticket[description]", remarks || "");
    if (file) {
      formData.append("customer_ticket[customer_uploaded_document]", file as Blob);
    }
    try {
      setIsLoading(true);
      const baseUrl = process.env.REACT_APP_KMS_BASE_URL;
      const path = `api/customers/customer_tickets/follow_up_ticket`;
      const headers = {
        "Content-Type": "multipart/form-data",
        Accept: "application/json",
        token: localStorage.getItem("token") || "",
      };
      const url = `${baseUrl}/${path}`;
      const response = await axios.post(url, formData, { headers });
      toast.success("Another ticket has been created successfully.");
      setTimeout(() => {
        const ticketId = response?.data?.id;
        if(ticketId){
          navigate(`/queries/${ticketId}`, {replace: true});
        } else {
          navigate(WebRoutes.QUERIES, {replace: true});
        }
      }, 2000);
    } catch (error) {
      console.error("Error submitting request:", error);
      alert("Failed to submit request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    const userId =
      localStorage.getItem("user_id") || sessionStorage.getItem("user_id");

    if (!userId) {
      navigate(WebRoutes.BOOKING_STEP_ONE);
    }
  }, [navigate]);

  return (
    <Container maxWidth="sm" sx={{ padding: 2 }}>
      {/* Header Section */}
      <HeaderWithBackButton title="Add New Ticket" />

      {/* Issue Type Dropdown */}
      <Typography
        mb={1}
        sx={{
          fontSize: ComponentConfig.fonts.bodyRegular.fontSize,
          fontWeight: ComponentConfig.fonts.bodyRegular.fontWeight,
        }}
      >
        Issue Type
      </Typography>
      <Select
        value={""}
        fullWidth
        displayEmpty
        disabled={true}
        IconComponent={() => (
          <></>
        )}
        sx={{
          borderRadius: ComponentConfig.ButtonBorderRadius,
          borderColor: Colors.greenBorderColor,
          mb: 2,
        }}
      >
        <MenuItem value="" disabled>
          {data?.issue}
        </MenuItem>
      </Select>

      {/* Upload Document Section */}
      <Typography
        sx={{
          fontSize: ComponentConfig.fonts.bodyRegular.fontSize,
          fontWeight: ComponentConfig.fonts.bodyRegular.fontWeight,
        }}
        mb={1}
      >
        Upload Document
      </Typography>
      <Box
        sx={{
          border: "1px dashed #C4C4C4",
          borderRadius: "8px",
          padding: "24px",
          textAlign: "center",
          mb: 2,
          position: "relative",
        }}
      >
        {file ? (
          <Typography variant="body2" color="green">
            {file.name} uploaded successfully!
          </Typography>
        ) : (
          <>
            <img src="/icons/document_pdf.svg" alt="arrow-down" />
            <Typography variant="body2" mt={1}>
              Upload Document
            </Typography>
          </>
        )}
        <input
          type="file"
          accept="application/pdf"
          onChange={handleFileUpload}
          style={{
            position: "absolute",
            opacity: 0,
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
            cursor: "pointer",
          }}
        />
      </Box>

      {/* Remarks Section */}
      <Typography
        sx={{
          fontSize: ComponentConfig.fonts.bodyRegular.fontSize,
          fontWeight: ComponentConfig.fonts.bodyRegular.fontWeight,
        }}
        mb={1}
      >
        Remarks
      </Typography>
      <TextareaAutosize
        minRows={2}
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        placeholder="Add remarks..."
        style={{
          width: "100%",
          padding: "12px",
          borderRadius: ComponentConfig.ButtonBorderRadius,
          borderColor: Colors.grey,
          fontFamily: "inherit",
          outline: "none",
        }}
      />
      <Typography
        variant="caption"
        color={remarks.length > 100 ? "error" : "text.secondary"}
        align="right"
        display="block"
        mt={1}
      >
        {remarks.length}/100
      </Typography>

      {/* Submit Button */}
      <PrimaryButton
        variant="contained"
        fullWidth
        text={isLoading ? "Submitting..." : "Submit"}
        textColor="white"
        textClassName="subtitle-medium"
        onTap={handleSubmit}
        disabled={isLoading}
        sx={{
          background: ComponentConfig.primaryButtonGradient,
        }}
      />
    </Container>
  );
};

export default FollowUpQuery;
